import { React, useState, useEffect } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Multiselect,
  EnhancedAutocomplete,
  CreateValidateTextField,
} from "..";
import { Dialog, DialogTitle, FilledInput, Snackbar, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

function ResearchPositionEdit(props) {

  const { userType, pid, updateTable, setUpdateTable, researchPosition } = props;
  const [title, setTitle] = useState(researchPosition.title);
  const [location, setLocation] = useState(researchPosition.location);
  const [facultyName, setFacultyName] = useState(researchPosition.facultyName);
  const [facultyPid, setFacultyPid] = useState(researchPosition.pid);
  const [skills, setSkills] = useState(researchPosition.skills);

  const [researchPositionDetails, setResearchPositionDetails] = useState(researchPosition.details);
  const [startDate, setStartDate] = useState(researchPosition.startDate);
  const [endDate, setEndDate] = useState(researchPosition.endDate);
  const [projectURL, setProjectURL] = useState(researchPosition.projectURL);

  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [researchPositionCompensation, setResearchPositionCompensation] = useState(researchPosition.researchPositionCompensation);
  const [researchPositionType, setResearchPositionType] = useState(researchPosition.researchPositionType);
  const [researchPositionResearchArea, setResearchPositionResearchArea] = useState(researchPosition.researchPositionResearchArea);
  const [locations, setLocations] = useState(researchPosition.locations);

  const [availableSkills, setAvailableSkills] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableCompensations, setAvailableCompensations] = useState([]);
  const [availableJobTypes, setAvailableJobTypes] = useState([]);
  const [availableResearchAreas, setAvailableResearchAreas] = useState([]);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, "0"); // add 1 to month because getMonth() returns 0-based index, and pad with leading zero if necessary
  const currentDay = currentDate.getDate().toString().padStart(2, "0"); // pad with leading zero if necessary
  const formattedCurrentDate = `${currentYear}-${currentMonth}-${currentDay}`;

  const nextDate = new Date();
  nextDate.setDate(currentDate.getDate() + 1);
  const nextYear = nextDate.getFullYear();
  const nextMonth = (nextDate.getMonth() + 1).toString().padStart(2, "0"); // add 1 to month because getMonth() returns 0-based index, and pad with leading zero if necessary
  const nextDay = nextDate.getDate().toString().padStart(2, "0"); // pad with leading zero if necessary
  const formattedNextDate = `${nextYear}-${nextMonth}-${nextDay}`;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    setTitle(researchPosition.title);
    setLocation(researchPosition.location);
    setLocations([researchPosition.location]);
    setFacultyName(researchPosition.facultyName);
    setFacultyPid(researchPosition.pid);
    setResearchPositionCompensation(researchPosition.researchPositionCompensation);
    setResearchPositionType(researchPosition.researchPositionType);
    setResearchPositionResearchArea(researchPosition.researchPositionResearchArea);
    setSkills(researchPosition.skills);
    setResearchPositionDetails(researchPosition.details);
    setProjectURL(researchPosition.projectURL);
    setStartDate(researchPosition.startDate);
    setEndDate(researchPosition.endDate);
  }, [researchPosition, openEditDialog]);

  const handleEditResearchPosition = (event, draft) => {
    event.preventDefault();

    const body = new FormData();
    body.append("pid", userType === "admin" ? facultyPid : pid);
    body.append("positionId", researchPosition.positionId);
    body.append("facultyId", researchPosition.facultyId);
    body.append("userType", userType);
    body.append("title", title);
    body.append("facultyName", facultyName);
    body.append("facultyPid", userType === "admin" ? facultyPid : pid);
    body.append("researchPositionDetails", researchPositionDetails);
    body.append("startDate", startDate.toString());
    body.append("endDate", endDate.toString());
    body.append("projectUrl", projectURL);
    body.append("researchPositionCompensation", researchPositionCompensation);
    body.append("skills", skills);
    body.append("researchPositionJobType", researchPositionType);
    body.append("researchPositionResearchArea", researchPositionResearchArea);
    body.append("location", locations);
    body.append("draft", draft);

    axios.post("/faculty/editResearchPosition", body).then((res) => {
      const data = res.data;
      if (data.success) {
        resetHooks();
        setOpenEditDialog(false);
        setUpdateTable(!updateTable);
      } else {
        setSnackbarMessage(data.message);
        setSnackbarOpen(true);
      }
    });
  };

  useEffect(() => {
    axios.get("/faculty/skills").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableSkills(data.skills);
      } else {
        return setAvailableSkills([]);
      }
    });
  }, []);

  useEffect(() => {
    axios.get("/faculty/locations").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableLocations(data.locations);
      } else {
        return setAvailableLocations([]);
      }
    });
  }, []);

  useEffect(() => {
    axios.get("/faculty/compensations").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableCompensations(data.compensations);
      } else {
        return setAvailableCompensations([]);
      }
    });
  }, []);

  useEffect(() => {
    axios.get("/faculty/jobTypes").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableJobTypes(data.jobTypes);
      } else {
        return setAvailableJobTypes([]);
      }
    });
  }, []);

  useEffect(() => {
    axios.get("/faculty/researchAreas").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableResearchAreas(data.researchAreas);
      } else {
        return setAvailableResearchAreas([]);
      }
    });
  }, []);

  function makeMultiselect(type) {
    let available = {};
    let value, setter;
    if (type === "Compensation") {
      available = availableCompensations;
      value = researchPositionCompensation;
      setter = setResearchPositionCompensation;
    } else if (type === "Position Types") {
      available = availableJobTypes;
      value = researchPositionType;
      setter = setResearchPositionType;
    } else if (type === "Research Areas") {
      available = availableResearchAreas;
      value = researchPositionResearchArea;
      setter = setResearchPositionResearchArea;
    } else {
      return;
    }

    return (
      <div className="[&>div>div]:mb-2">
        <Multiselect
          options={available}
          selectedValue={value}
          selectedSetter={setter}
        />
      </div>
    );
  }

  const resetHooks = () => {
    setTitle("");
    setLocation("");
    setFacultyName("");
    setFacultyPid("");
    setResearchPositionCompensation([]);
    setResearchPositionType([]);
    setResearchPositionResearchArea([]);
    setSkills([]);
    setStartDate(formattedCurrentDate);
    setEndDate(formattedNextDate);
    setResearchPositionDetails("");
    setProjectURL("");
    setLocations([]);
  };

  function showEditDialog() {
    return (
      <Dialog open={openEditDialog} maxWidth={false}>
        <DialogTitle className="ResearchPositionEdit-title mx-auto text-2xl font-bold font-brand-primary">
          Edit Research Position Posting
        </DialogTitle>
        <div className="ResearchPositionEdit-modalContainer flex w-fit mb-4 mx-4">
          <div className="ResearchPositionEdit-modalLeftContainer flex flex-col w-fit">
            <div className="ResearchPositionEdit-titleContainer flex flex-col w-96 mx-4 mb-2">
              <div className="ResearchPositionEdit-title text-lg font-brand-primary">
                Title *
              </div>
              <CreateValidateTextField
                placeholder="Junior Software Engineer"
                pattern="(?=.*\w).+"
                value={title}
                setter={setTitle}
                errorMessage={"Invalid Title."}
                required={true}
                maxLength={250}
              />
            </div>
            <div className="ResearchPositionEdit-applicationLinkContainer flex flex-col w-96 mx-4 mb-2">
              <div className="ResearchPositionEdit-applicationLinkTitle text-lg font-brand-primary">
                Project Url
              </div>
              <CreateValidateTextField
                placeholder="https://yoursite.com"
                pattern="(https:\/\/)?(www\.)?((?=[a-zA-Z])[a-zA-Z0-9_-]+(?<=[a-zA-Z])\.)+[a-zA-Z]{2,}\/?(\/?[a-zA-Z0-9-#?_]*)*|[a-zA-Z_.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                value={projectURL}
                setter={setProjectURL}
                errorMessage={"Invalid Link."}
                required={false}
                maxLength={1000}
              />
            </div>
            <div className="ResearchPositionEdit-locationContainer flex flex-col w-96 mx-4 mb-2">
              <div className="ResearchPositionEdit-locationTitle text-lg font-brand-primary">
                Location *
              </div>
              <div className="[&>div>div]:mt-1">
                  <EnhancedAutocomplete
                    placeholder="Select location"
                    options={availableLocations}
                    selectedValue={locations}
                    selectedSetter={setLocations}
                    isMultiSelect={false}
                  />
              </div>
            </div>
            <div className="ResearchPositionEdit-researchPositionDescriptionContainer flex flex-col w-96 mx-4 mb-2">
              <div className="ResearchPositionEdit-researchPositionDescriptionTitle text-lg font-brand-primary">
                Research Position Description *
              </div>
              <textarea
                value={researchPositionDetails}
                onChange={(event) => {
                  if (event.target.value.length <= 10000) {
                    setResearchPositionDetails(event.target.value);
                  }
                }}
                className="rounded-xl bg-white p-2 border border-gray-300"
                type="textarea"
                placeholder="Please add details about the research position here!"
                required={true}
              />
            </div>
            <div className="ResearchPositionEdit-dateContainer flex justify-between">
              <div className="ResearchPositionEdit-startDateContainer flex flex-col w-44 ml-4 mb-2">
                <div className="ResearchPositionEdit-startDateTitle text-lg font-brand-primary">
                  Posting Start Date *
                </div>
                <FilledInput
                  required
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disableUnderline={true}
                  className="rounded-xl bg-white border border-gray-300 h-11 [&>input]:pt-2"
                />
              </div>
              <div className="ResearchPositionEdit-endDateContainer flex flex-col w-44 mr-4 mb-2">
                <div className="ResearchPositionEdit-endDateTitle text-lg font-brand-primary">
                  Posting End Date *
                </div>
                <FilledInput
                  required
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disableUnderline={true}
                  className="rounded-xl h-11 bg-white border border-gray-300 [&>input]:pt-2"
                />
              </div>
            </div>
          </div>
          <div className="ResearchPositionEdit-modalRightContainer flex flex-col w-fit">
            <div className="ResearchPositionEdit-researchPositionCompensationsContainer flex flex-col w-96 mx-4 mb-4">
              <div className="ResearchPositionEdit-researchPositionCompensationsTitle text-lg font-brand-primary">
                Research Position Compensations *:
                <div className="[&>div>div]:mt-1">
                  <EnhancedAutocomplete
                    placeholder="Select compensation(s)"
                    options={availableCompensations}
                    selectedValue={researchPositionCompensation}
                    selectedSetter={setResearchPositionCompensation}
                    isMultiSelect={false}
                  />
                </div>
              </div>
            </div>
            <div className="ResearchPositionEdit-researchPositionTypeContainer flex flex-col w-96 mx-4 mb-4">
              <div className="ResearchPositionEdit-researchPositionTypeTitle text-lg font-brand-primary">
                Research Position Type(s) *:
                <div className="[&>div>div]:mt-1">
                  <EnhancedAutocomplete
                    placeholder="Select Research Position type(s)"
                    options={availableJobTypes}
                    selectedValue={researchPositionType}
                    selectedSetter={setResearchPositionType}
                    isMultiSelect={true}
                  />
                </div>
              </div>
            </div>
            <div className="ResearchPositionEdit-researchPositionResearchAreaContainer flex flex-col w-96 mx-4 mb-4">
              <div className="ResearchPositionEdit-researchPositionResearchAreaTitle text-lg font-brand-primary">
                Research Position Research Area(s) *:
                <div className="[&>div>div]:mt-1">
                  <EnhancedAutocomplete
                    placeholder="Select research(s)"
                    options={availableResearchAreas}
                    selectedValue={researchPositionResearchArea}
                    selectedSetter={setResearchPositionResearchArea}
                    isMultiSelect={true}
                  />
                </div>
              </div>
            </div>
            <div className="ResearchPositionEdit-researchPositionSkillsContainer flex flex-col w-96 mx-4 mb-4">
              <div className="ResearchPositionEdit-researchPositionSkillsTitle text-lg font-brand-primary">
                Research Position Skills:
                <div className="[&>div>div]:mt-1">
                  <EnhancedAutocomplete
                    placeholder="Select skill(s)"
                    options={availableSkills}
                    selectedValue={skills}
                    selectedSetter={setSkills}
                    isMultiSelect={true}
                  />
                </div>
              </div>
            </div>
            {//maybe PID HERE
            userType == "admin" && (
              <div className="ResearchPositionEdit-facultyContainer flex flex-col w-96 mx-4 mb-2">
                <div className="ResearchPositionEdit-facultyTitle text-lg font-brand-primary">
                  Faculty First and Last Name (autofilled)*
                </div>
                <CreateValidateTextField
                  placeholder="Faculty"
                  pattern="(?=.*\w).+"
                  value={facultyName}
                  setter={setFacultyName}
                  errorMessage={"Invalid Faculty Name."}
                  required={true}
                  disabled={true}
                  maxLength={250}
                />
                <div className="ResearchPositionEdit-facultyPid text-lg font-brand-primary">
                  Faculty pid (autofilled)*
                </div>
                <CreateValidateTextField
                  placeholder="fnamelname"
                  pattern="(?=.*\w).+"
                  value={facultyPid}
                  setter={setFacultyPid}
                  errorMessage={"Invalid Faculty pid."}
                  required={true}
                  disabled={true}
                />
              </div>
            )}
          </div>
        </div>
        <div className="ResearchPositionEdit-buttonContainer flex justify-between px-8 mb-4">
          <button
            type="button"
            className="primary-button bg-brand-secondary mb-2 h-11 pt-3"
            onClick={(event) => handleEditResearchPosition(event, true)}
          >
            Save As Draft
          </button>
          <button
            type="button"
            className="primary-button bg-brand-quaternary mb-2 h-11 pt-3"
            onClick={(event) => handleEditResearchPosition(event, false)}
          >
            {researchPosition.draft === true ? "Publish Research Position" : "Update Research Position"}
          </button>
          <button
            type="button"
            className="primary-button bg-brand-primary  mb-2 h-11 pt-3"
            onClick={resetHooks}
          >
            Reset
          </button>
          <button
            type="button"
            className="primary-button bg-brand-primary mb-2 h-11 pt-3"
            onClick={() => {
              setOpenEditDialog(false);
              resetHooks();
            }}
          >
            Cancel
          </button>
        </div>
        <Snackbar
          open={snackbarOpen}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={6000}
          onClose={() => {
            setSnackbarOpen(false);
            setSnackbarMessage("");
          }
          }
        >
          <Alert
            onClose={() => {
              setSnackbarOpen(false);
              setSnackbarMessage("");
            }
            }
            severity="error"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  }

  return (
    <div className="ResearchPositionEdit-container">
      {showEditDialog()}
      <button type="button" onClick={() => setOpenEditDialog(true)}>
        <EditIcon fontSize="large" className="fill-brand-quaternary" />
      </button>
    </div>
  );
}

ResearchPositionEdit.propTypes = {
  userType: PropTypes.string.isRequired,
  pid: PropTypes.string.isRequired,
  updateTable: PropTypes.bool.isRequired,
  setUpdateTable: PropTypes.func.isRequired,
  researchPosition: PropTypes.shape({
    positionId: PropTypes.number,
    facultyId: PropTypes.number,
    title: PropTypes.string,
    location: PropTypes.string,
    facultyName: PropTypes.string,
    pid: PropTypes.string,
    draft: PropTypes.bool,
    details: PropTypes.string,
    projectURL: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    researchPositionType: PropTypes.arrayOf(PropTypes.string),
    researchPositionCompensation: PropTypes.arrayOf(PropTypes.string),
    researchPositionResearchArea: PropTypes.arrayOf(PropTypes.string),
    skills: PropTypes.arrayOf(PropTypes.string),
    locations: PropTypes.arrayOf(PropTypes.string),
    viewCount: PropTypes.number,
    applyCount: PropTypes.number,
    favorited: PropTypes.string,
  }),
};

export default ResearchPositionEdit;
